/**
 * Dashboards API client — wraps apiFetch for dashboard-specific endpoints.
 * DO NOT import from reports-client.ts here; this file is intentionally separate
 * to avoid conflicts with parallel hito agents.
 *
 * Endpoints:
 *  §5.2 POST /dashboards/:tenantReportId/widgets/:widgetId/execute
 *  §5.3 POST /dashboards/:tenantReportId/execute
 */

import { apiFetch } from '@/lib/api/client.js';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared wire schemas
// ---------------------------------------------------------------------------

const WidgetExecutionMetaWireSchema = z.object({
  execution_id: z.string().optional(),
  executed_at: z.string().optional(),
  duration_ms: z.number().optional(),
  cache: z
    .object({
      hit: z.boolean(),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// F1. Widget execute — §5.2
// ---------------------------------------------------------------------------

const WidgetExecuteResponseWireSchema = z.object({
  rows: z.array(z.record(z.unknown())),
  meta: WidgetExecutionMetaWireSchema.optional(),
  cache_hit: z.boolean().optional(),
  semi_join_count: z.number().optional(),
  duration_ms: z.number().optional(),
  applied_query: z.string().optional().nullable(),
});

export interface WidgetExecuteRequest {
  global_params: Record<string, unknown>;
  widget_params?: Record<string, unknown>;
  view_state?: Record<string, unknown>;
  fresh?: boolean;
}

export interface WidgetExecuteResponse {
  rows: ReadonlyArray<Record<string, unknown>>;
  meta: {
    executionId: string | undefined;
    executedAt: string | undefined;
    durationMs: number | undefined;
    cacheHit: boolean;
  };
  semiJoinCount: number | undefined;
  appliedQuery: string | undefined;
}

function mapWidgetExecuteResponse(
  wire: z.infer<typeof WidgetExecuteResponseWireSchema>,
): WidgetExecuteResponse {
  return {
    rows: wire.rows,
    meta: {
      executionId: wire.meta?.execution_id,
      executedAt: wire.meta?.executed_at,
      durationMs: wire.meta?.duration_ms ?? wire.duration_ms,
      cacheHit: wire.meta?.cache?.hit ?? wire.cache_hit ?? false,
    },
    semiJoinCount: wire.semi_join_count,
    appliedQuery: wire.applied_query ?? undefined,
  };
}

export async function executeWidget(
  tenantReportId: string,
  widgetId: string,
  body: WidgetExecuteRequest,
  signal?: AbortSignal,
): Promise<WidgetExecuteResponse> {
  const raw = await apiFetch<unknown>(`/dashboards/${tenantReportId}/widgets/${widgetId}/execute`, {
    method: 'POST',
    body: JSON.stringify(body),
    ...(signal !== undefined ? { signal } : {}),
  });
  const parsed = WidgetExecuteResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Widget execute response shape mismatch: ${parsed.error.message}`);
  }
  return mapWidgetExecuteResponse(parsed.data);
}

// ---------------------------------------------------------------------------
// F2. Dashboard execute — §5.3
// ---------------------------------------------------------------------------

const WidgetResultSuccessWireSchema = z.object({
  status: z.literal('success'),
  rows: z.array(z.record(z.unknown())),
  meta: WidgetExecutionMetaWireSchema.optional(),
  cache_hit: z.boolean().optional(),
  semi_join_count: z.number().optional(),
  duration_ms: z.number().optional(),
  applied_query: z.string().optional().nullable(),
});

const WidgetResultErrorWireSchema = z.object({
  status: z.literal('error'),
  error: z.object({
    code: z.string(),
    message: z.string(),
    request_id: z.string().optional(),
  }),
});

const WidgetResultWireSchema = z.discriminatedUnion('status', [
  WidgetResultSuccessWireSchema,
  WidgetResultErrorWireSchema,
]);

const DashboardExecuteResponseWireSchema = z.object({
  widget_results: z.record(WidgetResultWireSchema),
  dashboard_meta: z.object({
    executed_at: z.string(),
    total_widgets: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    total_duration_ms: z.number(),
  }),
});

export interface DashboardExecuteRequest {
  global_params: Record<string, unknown>;
  /** Per-widget parameter overrides: { [widgetId]: { paramName: value, ... } } */
  widget_params?: Record<string, Record<string, unknown>>;
  view_state?: Record<string, unknown>;
  fresh?: boolean;
}

export type WidgetResultSuccess = {
  status: 'success';
  rows: ReadonlyArray<Record<string, unknown>>;
  meta: {
    executionId: string | undefined;
    executedAt: string | undefined;
    durationMs: number | undefined;
    cacheHit: boolean;
  };
  semiJoinCount: number | undefined;
  appliedQuery: string | undefined;
};

export type WidgetResultError = {
  status: 'error';
  error: {
    code: string;
    message: string;
    requestId: string | undefined;
  };
};

export type WidgetResult = WidgetResultSuccess | WidgetResultError;

export interface DashboardExecuteResponse {
  widgetResults: Record<string, WidgetResult>;
  dashboardMeta: {
    executedAt: string;
    totalWidgets: number;
    succeeded: number;
    failed: number;
    totalDurationMs: number;
  };
}

function mapWidgetResult(wire: z.infer<typeof WidgetResultWireSchema>): WidgetResult {
  if (wire.status === 'error') {
    return {
      status: 'error',
      error: {
        code: wire.error.code,
        message: wire.error.message,
        requestId: wire.error.request_id,
      },
    };
  }
  return {
    status: 'success',
    rows: wire.rows,
    meta: {
      executionId: wire.meta?.execution_id,
      executedAt: wire.meta?.executed_at,
      durationMs: wire.meta?.duration_ms ?? wire.duration_ms,
      cacheHit: wire.meta?.cache?.hit ?? wire.cache_hit ?? false,
    },
    semiJoinCount: wire.semi_join_count,
    appliedQuery: wire.applied_query ?? undefined,
  };
}

export async function executeDashboard(
  tenantReportId: string,
  body: DashboardExecuteRequest,
): Promise<DashboardExecuteResponse> {
  const raw = await apiFetch<unknown>(`/dashboards/${tenantReportId}/execute`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const parsed = DashboardExecuteResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Dashboard execute response shape mismatch: ${parsed.error.message}`);
  }
  const d = parsed.data;
  const widgetResults: Record<string, WidgetResult> = {};
  for (const [id, result] of Object.entries(d.widget_results)) {
    widgetResults[id] = mapWidgetResult(result);
  }
  return {
    widgetResults,
    dashboardMeta: {
      executedAt: d.dashboard_meta.executed_at,
      totalWidgets: d.dashboard_meta.total_widgets,
      succeeded: d.dashboard_meta.succeeded,
      failed: d.dashboard_meta.failed,
      totalDurationMs: d.dashboard_meta.total_duration_ms,
    },
  };
}
